import { useEffect, useMemo, useState } from 'react';
import useVideoContext from '../useVideoContext/useVideoContext';
import type { AgoraParticipantLike } from '../../types';
import useParticipants from '../useParticipants/useParticipants';

type VolumeIndicatorRow = { uid: number | string; level: number };

function parseVolumeRows(args: any[]): VolumeIndicatorRow[] {
  const first = args?.[0];
  if (Array.isArray(first)) return first as any;
  return [];
}

export default function useDominantSpeaker(includeNull = false): AgoraParticipantLike | null {
  const { room } = useVideoContext();
  const participants = useParticipants();
  const [dominantUid, setDominantUid] = useState<number | null>(null);

  const allParticipants = useMemo(() => {
    if (!room) return [];
    return [room.localParticipant, ...participants];
  }, [room, participants]);

  useEffect(() => {
    if (!room) {
      setDominantUid(null);
      return;
    }

    const client = room.client;
    try {
      client.enableAudioVolumeIndicator?.();
    } catch {
      /* ignore */
    }

    const onVolume = (...args: any[]) => {
      const rows = parseVolumeRows(args);
      if (!rows.length) {
        if (includeNull) setDominantUid(null);
        return;
      }

      let bestUid: number | null = null;
      let bestLevel = -1;
      for (const r of rows) {
        const uid = typeof r.uid === 'number' ? r.uid : parseInt(String(r.uid), 10);
        const level = Number((r as any).level ?? 0);
        if (!Number.isFinite(uid)) continue;
        if (level > bestLevel) {
          bestLevel = level;
          bestUid = uid;
        }
      }

      // Agora `level` is 0..255; treat very low levels as silence to reduce flicker.
      if (bestUid !== null && bestLevel >= 5) {
        setDominantUid(bestUid);
      } else if (includeNull) {
        setDominantUid(null);
      }
    };

    client.on('volume-indicator', onVolume as any);
    return () => {
      client.off('volume-indicator', onVolume as any);
    };
  }, [room, includeNull]);

  const dominantSpeaker = useMemo(() => {
    if (!room) return null;
    if (dominantUid === null) return includeNull ? null : room.localParticipant ?? null;
    return allParticipants.find(p => p.uid === dominantUid) ?? (includeNull ? null : room.localParticipant ?? null);
  }, [room, dominantUid, allParticipants, includeNull]);

  return dominantSpeaker;
}
